// Aba Scanner PDF (0.49, grátis): escaneie documentos, recibos, cadernos e quadros e mande em PDF.
// No app novo, o scanner do próprio celular acha as bordas da folha, endireita e tira sombras sozinho.
// Também: várias páginas num PDF, documento de identidade (frente e verso na mesma folha), filtros, pastas,
// assinatura, e a IA do usuário lê o texto, resume e responde perguntas sobre o documento.
import { useCallback, useEffect, useState } from 'react';
import { ActivityIndicator, Alert, Image, Linking, Modal, Platform, Pressable, ScrollView, Share, Text, TextInput, View, useWindowDimensions } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { Btn, Chip, Field, Screen, Section, ActionSheet, useVoltarAndroid, type Action } from '../components/ui';
import { C, alpha, useCores } from '../components/theme';
import { Ordenavel } from '../components/Arrastar';
import { AcoesItem, acoesDoMenu, useAcoes } from '../components/Acoes';
import { Arquivados, useSelecao } from '../components/Selecao';
import { useOrganizar } from '../lib/organizar';
import { paraLixeira } from '../lib/lixeira';
import { useBotaoNovo } from '../lib/botaoNovo';
import { RelatoriosGerados } from '../components/RelatoriosGerados';
import { useCameraFoto } from '../components/CameraFoto';
import { useAnexar } from '../components/MenuAnexo';
import { apagarAnexo } from '../lib/anexos';
import type { Anexo } from '../store/types';
import { AjustarCantos } from '../components/AjustarCantos';
import { GuardarEm } from '../components/GuardarEm';
import { DesenhoSheet, DesenhoView } from '../components/Desenho';
import { useDoc } from '../store/store';
import type { FiltroScan, ScanDoc, ScanPagina } from '../store/types';
import type { Cantos } from '../lib/scanImagem';
import { br, brYmd, norm, ymd } from '../lib/date';
import { t } from '../lib/i18n';
import { useIAPronta } from '../lib/ia';
import { NO_NAVEGADOR, escanearSistema, escolherFotos, temScannerSistema, temTratamento } from '../lib/scanArquivos';
import {
  adicionarPaginas, apagarDoc, apagarPagina, apagarPasta, compartilharPdf, copiaEmDia, criarDoc, destinoScan, escolherPasta, estaAqui, fonteBruta, fontePagina,
  guardarPdf, lerTexto, mudarDestino, mudarDoc, nomeDestino, novaPasta, ordenarPaginas, paginasDeFotos, pastaAtual, perguntarAoDoc, renomearPasta, resumir,
  retratar, textoDoDoc, cantosAutomaticos, type Foto,
} from '../lib/scanner';
import { NOME, atualizarNuvem, contaDe, useNuvem } from '../lib/nuvem';
import { useRouter } from 'expo-router';
import type { DestinoScan } from '../store/types';
import { useCaixas } from '../lib/caixas';

const falha = (e: any) => Alert.alert(t('Não foi possível'), String(e?.message ?? e));
const NOMES_FILTRO: Record<FiltroScan, string> = { documento: 'Documento', original: 'Original', cinza: 'Cinza', pb: 'Preto e branco' };
const paginasTxt = (n: number) => (n === 1 ? t('1 página') : t('{n} páginas', { n }));
const confirmar = (titulo: string, msg: string, sim: string) => new Promise<boolean>(ok => Alert.alert(titulo, msg, [
  { text: t('Concluir'), style: 'cancel', onPress: () => ok(false) }, { text: sim, onPress: () => ok(true) }], { cancelable: false }));

export default function Scanner({ item, onBack }: { item?: string; onBack: () => void }) {
  const { corDe } = useCores();
  const cor = corDe('scanner');
  const cx = useCaixas();   // "Tamanho das caixas" (Grande = como antes)
  const { pastas = [], docs = [], guardar = 'aparelho' } = useDoc('scanner');
  const [pasta, setPasta] = useState<string | null>(null);
  const [busca, setBusca] = useState('');
  const [aberto, setAberto] = useState<string | null>(item ?? null);
  const [ocupado, setOcupado] = useState<string | null>(null);
  const [nomeModal, setNomeModal] = useState<{ titulo: string; valor: string; ok: (s: string) => void } | null>(null);
  const [menu, setMenu] = useState<{ titulo: string; acoes: Action[] } | null>(null);
  const camera = useCameraFoto();
  // 0.72 (11g): Importar fotos pelo menu de anexos do app (Fotos · Tirar foto · Arquivos), várias de uma vez
  const anexo = useAnexar({ cor, semNuvem: true });
  const [ajuste, setAjuste] = useState<{ uri: string; titulo: string; inicial: Cantos | null; ok: (c: Cantos | null | undefined) => void } | null>(null);

  useEffect(() => { atualizarNuvem().catch(() => {}); }, []);
  useEffect(() => { if (item) setAberto(item); }, [item]);

  /** Pede os 4 cantos de uma foto (câmera comum ou navegador). undefined = cancelou. */
  const pedirCantos = (uri: string, titulo: string, inicial: Cantos | null) => new Promise<Cantos | null | undefined>(ok => setAjuste({ uri, titulo, inicial, ok }));

  /** Tira as fotos (scanner do sistema, câmera comum ou navegador). */
  async function capturar(tipo: 'doc' | 'id'): Promise<{ fotos: Foto[]; filtro: FiltroScan } | null> {
    if (temScannerSistema()) {
      const uris = await escanearSistema(tipo === 'id' ? 2 : undefined);
      return uris ? { fotos: uris.slice(0, tipo === 'id' ? 2 : 100).map(uri => ({ uri })), filtro: 'original' } : null;
    }
    const fotos: Foto[] = [];
    for (let i = 0; ; i++) {
      const dica = tipo === 'id' ? (i === 0 ? t('Frente do documento') : t('Verso do documento')) : t('Página {n}', { n: i + 1 });
      let uri: string | null = null;
      if (NO_NAVEGADOR) uri = (await escolherFotos(true))[0] ?? null;
      else uri = (await camera.abrir({ dica }))?.uri ?? null;
      if (!uri) break;
      let cantos: Cantos | null | undefined = null;
      if (temTratamento()) {
        setOcupado(t('Procurando a folha...'));
        const auto = await cantosAutomaticos(uri);
        setOcupado(null);
        cantos = await pedirCantos(uri, dica, auto);
      }
      if (cantos === undefined) continue;   // cancelou o recorte: tira de novo
      fotos.push({ uri, cantos });
      if (tipo === 'id' && fotos.length >= 2) break;
      // no navegador, a janela de escolher foto só abre depois de um toque: por isso a pergunta vem sempre lá
      const mais = tipo === 'id'
        ? (NO_NAVEGADOR ? await confirmar(t('Frente pronta'), t('Agora o verso do documento.'), t('Escanear o verso')) : true)
        : await confirmar(t('Página escaneada'), t('Quer escanear mais uma página?'), t('Mais uma'));
      if (!mais) break;
    }
    return fotos.length ? { fotos, filtro: tipo === 'id' ? 'original' : 'documento' } : null;
  }

  /** Fotos para importar: no navegador, o seletor de imagens; no celular, o menu Fotos · Tirar foto · Arquivos.
   *  temporarios: as cópias que o menu guardou (saem depois de virar páginas). */
  async function importarFotos(): Promise<{ uris: string[]; temporarios: Anexo[] }> {
    if (NO_NAVEGADOR) return { uris: await escolherFotos(), temporarios: [] };
    const r = await anexo.anexar({ tipo: 'imagem', titulo: t('Importar fotos'), varios: true });
    const as = r ? [r.anexo, ...(r.outros ?? [])].filter(a => !!a.uri) : [];
    return { uris: as.map(a => a.uri!).slice(0, 40), temporarios: as };
  }

  async function criar(tipo: 'doc' | 'id', origem: 'camera' | 'galeria') {
    let temporarios: Anexo[] = [];
    try {
      let r: { fotos: Foto[]; filtro: FiltroScan } | null;
      if (origem === 'galeria') {
        const im = await importarFotos();
        temporarios = im.temporarios;
        r = im.uris.length ? { fotos: im.uris.map(uri => ({ uri })), filtro: 'documento' } : null;
      } else r = await capturar(tipo);
      if (!r) return;
      const fotos = r.fotos;
      const id = String(Date.now()) + Math.random().toString(36).slice(2, 6);
      const pags = await paginasDeFotos(id, fotos, r.filtro, i => setOcupado(t('Preparando a página {n} de {total}...', { n: i + 1, total: fotos.length })), origem === 'galeria');
      criarDoc(tipo, pasta, pags, id);
      setAberto(id);
    } catch (e) { falha(e); } finally { setOcupado(null); temporarios.forEach(apagarAnexo); }
  }

  // 0.65: arquivados e fixados (documentos e pastas), seleção, lixeira e o + flutuante
  const org = useOrganizar('scanner');
  const orgP = useOrganizar('scanner-pasta');
  const { enviar } = useAcoes();
  const sep = org.separar(docs, d => d.id);
  const sepP = orgP.separar(pastas, p => p.id);
  const daPasta = (d: ScanDoc) => (pasta ? d.pasta === pasta : true);
  const q = norm(busca.trim());
  const lista = sep.ativos.filter(daPasta).filter(d => !q || norm(d.nome).includes(q) || norm(textoDoDoc(d)).includes(q));
  const excluirDoc = (d: ScanDoc) => { paraLixeira('scanner', 'lista', d.nome, d, { doc: 'scanner', campo: 'docs' }); apagarDoc(d.id); };
  const sel = useSelecao({ itens: lista, chave: d => d.id, cor, onArquivar: ids => org.arquivar(ids),
    onExcluir: ids => ids.forEach(id => { const d = docs.find(x => x.id === id); if (d) excluirDoc(d); }) });

  const pedirNovaPasta = () => setNomeModal({ titulo: t('Nova pasta'), valor: '', ok: n => { if (n.trim()) setPasta(novaPasta(n).id); } });
  const excluirPasta = (p: { id: string; nome: string }) => {
    paraLixeira('scanner', 'scanner-pasta', p.nome, p, { docs: docs.filter(d => d.pasta === p.id).map(d => d.id) });
    apagarPasta(p.id); if (pasta === p.id) setPasta(null);
  };
  const menuPasta = (p: { id: string; nome: string }) => setMenu({ titulo: p.nome, acoes: [
    ...acoesDoMenu(enviar, { fixar: { on: orgP.fixado(p.id), alternar: () => orgP.alternarFixo(p.id) } }),
    { label: '📦 ' + t('Arquivar'), onPress: () => { orgP.arquivar(p.id); if (pasta === p.id) setPasta(null); } },
    { label: '✏️ ' + t('Renomear'), onPress: () => setNomeModal({ titulo: t('Renomear pasta'), valor: p.nome, ok: n => { if (n.trim()) renomearPasta(p.id, n); } }) },
    { label: '🗑 ' + t('Apagar a pasta (os documentos continuam)'), destructive: true, onPress: () => Alert.alert(t('Excluir {nome}?', { nome: p.nome }), t('Fica 30 dias na lixeira.'), [
      { text: t('Cancelar'), style: 'cancel' }, { text: t('Excluir'), style: 'destructive', onPress: () => excluirPasta(p) }]) },
  ] });

  const docAberto = docs.find(d => d.id === aberto);
  useBotaoNovo(docAberto ? null : { rotulo: t('Adicionar'), fn: () => setMenu({ titulo: t('Adicionar'), acoes: [
    { label: '📷 ' + t('Escanear documento'), onPress: () => criar('doc', 'camera') },
    { label: '🪪 ' + t('Identidade'), onPress: () => criar('id', 'camera') },
    { label: '🖼️ ' + t('Importar fotos'), onPress: () => criar('doc', 'galeria') },
    { label: '📁 ' + t('Nova pasta'), onPress: pedirNovaPasta },
  ] }) });
  const telas = (
    <>
      {camera.tela}
      {anexo.tela}
      <AjustarCantos uri={ajuste?.uri ?? null} titulo={ajuste?.titulo} inicial={ajuste?.inicial} cor={cor}
        onCancelar={() => { ajuste?.ok(undefined); setAjuste(null); }} onAplicar={c => { ajuste?.ok(c); setAjuste(null); }} />
      <Ocupado msg={ocupado} cor={cor} />
      <NomeModal dados={nomeModal} cor={cor} onFechar={() => setNomeModal(null)} />
      <ActionSheet title={menu?.titulo ?? ''} actions={menu?.acoes ?? null} onClose={() => setMenu(null)} />
    </>
  );

  if (docAberto) {
    return (
      <View style={{ flex: 1 }}>
        <DocAberto key={docAberto.id} d={docAberto} cor={cor} onBack={() => setAberto(null)} setOcupado={setOcupado} capturar={capturar} />
        {telas}
      </View>
    );
  }

  return (
    <View style={{ flex: 1 }}>
      <Screen title={t('Scanner PDF')} onBack={onBack}>
        {/* botões de escanear */}
        <Pressable onPress={() => criar('doc', 'camera')} style={{ backgroundColor: cor, borderRadius: 18, padding: 18, flexDirection: 'row', alignItems: 'center', gap: 14 }}>
          <Text style={{ fontSize: 34 }}>📷</Text>
          <View style={{ flex: 1 }}>
            <Text style={{ color: '#fff', fontWeight: '800', fontSize: 18 }}>{t('Escanear documento')}</Text>
            <Text style={{ color: 'rgba(255,255,255,0.85)', fontSize: 13 }}>{t('Documentos, recibos, cadernos e quadros, com várias páginas')}</Text>
          </View>
        </Pressable>
        <View style={{ flexDirection: 'row', gap: 10 }}>
          <Pressable onPress={() => criar('id', 'camera')} style={{ flex: 1, backgroundColor: C.card, borderRadius: 16, padding: 14, gap: 4 }}>
            <Text style={{ fontSize: 24 }}>🪪</Text>
            <Text style={{ color: C.text, fontWeight: '700' }}>{t('Identidade')}</Text>
            <Text style={{ color: C.sub, fontSize: 12 }}>{t('Frente e verso na mesma folha')}</Text>
          </Pressable>
          <Pressable onPress={() => criar('doc', 'galeria')} style={{ flex: 1, backgroundColor: C.card, borderRadius: 16, padding: 14, gap: 4 }}>
            <Text style={{ fontSize: 24 }}>🖼️</Text>
            <Text style={{ color: C.text, fontWeight: '700' }}>{t('Importar fotos')}</Text>
            <Text style={{ color: C.sub, fontSize: 12 }}>{t('Da galeria ou dos arquivos')}</Text>
          </Pressable>
        </View>
        {!NO_NAVEGADOR && !temScannerSistema() && (
          <Text style={{ color: C.mute, fontSize: 12, textAlign: 'center' }}>
            {t('No app novo (versão 0.49), o celular acha as bordas da folha sozinho. Nesta versão, ajuste os 4 cantos com o dedo.')}
          </Text>
        )}

        <GuardarEm cor={cor} valor={guardar} onMudar={mudarDestino} titulo={t('Guardar os PDFs em')}
          dicaNuvem={nome => t('Cada documento novo vai para o {nome} quando você sai dele. As fotos das páginas ficam só neste dispositivo.', { nome })}
          dicaPasta={pasta => t('Na pasta "{pasta}" do celular. Cada documento novo vai para lá quando você sai dele.', { pasta })} />

        {/* pastas */}
        <ScrollView horizontal showsHorizontalScrollIndicator={false} contentContainerStyle={{ gap: 8, paddingVertical: 4 }}>
          <Chip small cor={cor} on={!pasta} label={t('Todos ({n})', { n: docs.length })} onPress={() => setPasta(null)} />
          {sepP.ativos.map(p => (
            <Pressable key={p.id} onLongPress={() => menuPasta(p)} delayLongPress={1000}>
              <Chip small cor={cor} on={pasta === p.id} label={`${orgP.fixado(p.id) ? '⭐ ' : ''}📁 ${p.nome} (${docs.filter(d => d.pasta === p.id).length})`}
                onPress={() => (pasta === p.id ? menuPasta(p) : setPasta(p.id))} />
            </Pressable>
          ))}
          <Chip small cor={cor} on={false} label={'+ ' + t('Pasta')} onPress={pedirNovaPasta} />
        </ScrollView>
        <Arquivados itens={sepP.arquivados} chave={p => p.id} cor={cor} qualificador={t('Pastas')} render={p => <Text style={{ color: C.text }} numberOfLines={1}>📁 {p.nome}</Text>}
          onRestaurar={p => orgP.restaurar(p.id)} onExcluir={p => Alert.alert(t('Excluir {nome}?', { nome: p.nome }), t('Fica 30 dias na lixeira.'), [
            { text: t('Cancelar'), style: 'cancel' }, { text: t('Excluir'), style: 'destructive', onPress: () => excluirPasta(p) }])} />

        {docs.length > 3 && <Field value={busca} onChangeText={setBusca} placeholder={'🔎 ' + t('Buscar pelo nome ou pelo texto')} />}

        {!docs.length && (
          <View style={{ backgroundColor: C.card, borderRadius: 16, padding: 18, gap: 8, alignItems: 'center' }}>
            <Text style={{ fontSize: 38 }}>📄</Text>
            <Text style={{ color: C.text, fontWeight: '700', fontSize: 16, textAlign: 'center' }}>{t('Seu scanner de bolso')}</Text>
            <Text style={{ color: C.sub, textAlign: 'center' }}>
              {t('Escaneie, deixe a imagem mais clara, organize em pastas e mande o PDF por WhatsApp, e-mail ou Drive. Com a sua IA ligada, o app lê o texto, resume e responde perguntas sobre o documento.')}
            </Text>
          </View>
        )}
        {!!docs.length && !lista.length && <Text style={{ color: C.sub, textAlign: 'center', marginTop: 10 }}>{t('Nada encontrado.')}</Text>}
        {!!lista.length && <Text style={{ color: C.mute, fontSize: 11, textAlign: 'center' }}>{t('Arraste para a direita para excluir, ou para a esquerda para arquivar.')}</Text>}
        {sel.barra}
        <Ordenavel itens={lista} chave={d => d.id} gap={cx.gap(10)} desligado onOrdem={() => {}} selecao={sel.prop} render={d => (
          <AcoesItem lembrar={{ aba: 'scanner', id: d.id, titulo: d.nome }} nome={d.nome} onExcluir={() => excluirDoc(d)} onArquivar={() => org.arquivar(d.id)} aviso={t('O PDF e as imagens saem deste dispositivo e da nuvem.')}
            fixar={{ on: org.fixado(d.id), alternar: () => org.alternarFixo(d.id) }}
            pacote={() => ({ titulo: d.nome, emoji: '📄', origem: 'scanner', texto: textoDoDoc(d) || t('{n} página(s)', { n: d.paginas.length }) })}>
            <Pressable onPress={() => setAberto(d.id)} style={{ backgroundColor: C.card, borderRadius: cx.raio(16), ...cx.pads(10), flexDirection: 'row', alignItems: 'center', gap: cx.gap(12) }}>
              <Miniatura d={d} p={d.paginas[0]} larg={cx.alt(52)} cor={cor} />
              <View style={{ flex: 1 }}>
                <Text style={{ color: C.text, fontWeight: '700' }} numberOfLines={2}>{d.tipo === 'id' ? '🪪 ' : ''}{d.nome}</Text>
                <Text style={{ color: C.sub, fontSize: 12 }}>
                  {br(new Date(d.criado))} · {paginasTxt(d.paginas.length)}{d.pasta ? ` · 📁 ${pastas.find(p => p.id === d.pasta)?.nome ?? ''}` : ''}
                </Text>
              </View>
              <Text style={{ color: C.mute, fontSize: 20 }}>›</Text>
            </Pressable>
          </AcoesItem>
        )} />
        <Arquivados itens={sep.arquivados.filter(daPasta)} chave={d => d.id} cor={cor} qualificador={t('Documentos')} render={d => <Text style={{ color: C.text }} numberOfLines={1}>📄 {d.nome}</Text>}
          onRestaurar={d => org.restaurar(d.id)} onExcluir={d => Alert.alert(t('Excluir {nome}?', { nome: d.nome }), t('O PDF e as imagens saem deste dispositivo e da nuvem.') + '\n\n' + t('Fica 30 dias na lixeira.'), [
            { text: t('Cancelar'), style: 'cancel' }, { text: t('Excluir'), style: 'destructive', onPress: () => excluirDoc(d) }])} />
        <RelatoriosGerados aba="scanner" cor={cor} />
        <View style={{ height: 90 }} />
      </Screen>
      {telas}
    </View>
  );
}

// ─── um documento aberto ───
function DocAberto({ d, cor, onBack, setOcupado, capturar }: {
  d: ScanDoc; cor: string; onBack: () => void; setOcupado: (s: string | null) => void;
  capturar: (tipo: 'doc' | 'id') => Promise<{ fotos: Foto[]; filtro: FiltroScan } | null>;
}) {
  const { width } = useWindowDimensions();
  const { pastas = [] } = useDoc('scanner');
  const temIA = useIAPronta();
  const [nome, setNome] = useState(d.nome);
  const [pagina, setPagina] = useState<number | null>(null);
  const [assinar, setAssinar] = useState(false);
  const [pergunta, setPergunta] = useState('');
  const [resposta, setResposta] = useState('');
  const [verTexto, setVerTexto] = useState(false);
  const [aqui, setAqui] = useState(true);
  useEffect(() => { setNome(d.nome); }, [d.id]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { estaAqui(d).then(setAqui).catch(() => {}); }, [d]);
  // 11d F: com o documento aberto, o + adiciona página (o mesmo "＋ Adicionar página" da tela)
  useBotaoNovo(aqui ? { rotulo: t('Adicionar página'), fn: () => { adicionar(); } } : null);
  const destino = destinoScan();
  useVoltarAndroid(() => { sair(); });   // 0.70 (texto 09, E4): o "voltar" do Android sai do documento como o "‹" (e envia ao destino)

  /** Ao sair: o documento novo vai sozinho para o destino escolhido (Drive, OneDrive ou a pasta do celular). */
  async function sair() {
    const novo = !d.salvo && d.paginas.length > 0 && aqui;
    if (novo && NO_NAVEGADOR && (destino === 'aparelho' || !contaDe(destino))) {
      const baixar = await new Promise<boolean>(ok => Alert.alert(t('Baixar o PDF?'), t('No navegador, as páginas ficam só enquanto esta página estiver aberta.'), [
        { text: t('Sair sem baixar'), style: 'cancel', onPress: () => ok(false) }, { text: t('Baixar'), onPress: () => ok(true) }]));
      if (baixar) await guardarPdf(d, 'aparelho').catch(falha);
      onBack();
      return;
    }
    onBack();
    if (novo) guardarPdf(d, destino, undefined, true).catch(() => {});
  }
  async function guardar() {
    try {
      const msg = await guardarPdf(d, destino, i => setOcupado(t('Montando o PDF: página {n} de {total}...', { n: i + 1, total: d.paginas.length })));
      setOcupado(null);
      if (msg) Alert.alert(t('Pronto'), msg);
    } catch (e) { setOcupado(null); falha(e); }
  }
  const salvarNome = () => { const n = nome.trim(); if (n && n !== d.nome) mudarDoc(d.id, { nome: n }); else setNome(d.nome); };

  const colunas = width > 700 ? 5 : 3;
  const largMini = Math.floor((Math.min(width, 900) - 32 - (colunas - 1) * 10) / colunas);
  const progresso = (rotulo: string) => (i: number, n: number) => setOcupado(`${rotulo} (${i + 1}/${n})`);

  async function adicionar() {
    try {
      const r = await capturar('doc');
      if (!r) return;
      const pags = await paginasDeFotos(d.id, r.fotos, r.filtro, i => setOcupado(t('Preparando a página {n} de {total}...', { n: i + 1, total: r.fotos.length })));
      adicionarPaginas(d.id, pags);
    } catch (e) { falha(e); } finally { setOcupado(null); }
  }
  async function enviar() {
    try {
      const tam = await compartilharPdf(d, i => setOcupado(t('Montando o PDF: página {n} de {total}...', { n: i + 1, total: d.paginas.length })));
      if (tam > 15 * 1024 * 1024 && !d.menor) Alert.alert(t('PDF grande'), t('O PDF passou de 15 MB. Se o WhatsApp ou o e-mail recusar, ligue "Arquivo menor".'));
    } catch (e) { falha(e); } finally { setOcupado(null); }
  }
  const router = useRouter();
  const semIA = () => Alert.alert(t('Precisa da sua IA'), t('Para ler o texto, resumir e perguntar, ligue a sua IA em Ajustes › Inteligência artificial (Claude, ChatGPT ou Gemini).'), [
    { text: t('Cancelar'), style: 'cancel' }, { text: t('Abrir Ajustes'), onPress: () => router.navigate({ pathname: '/ajustes', params: { sec: 'ia' } } as any) }]);
  async function ia(acao: 'texto' | 'resumo' | 'pergunta') {
    if (!temIA) return semIA();
    try {
      if (acao === 'texto') { await lerTexto(d.id, progresso(t('Lendo o texto'))); setVerTexto(true); }
      else if (acao === 'resumo') await resumir(d.id, progresso(t('Lendo o texto')));
      else {
        if (!pergunta.trim()) return;
        setResposta('');
        const r = await perguntarAoDoc(d.id, pergunta.trim(), progresso(t('Lendo o texto')));
        setResposta(r);
      }
    } catch (e) { falha(e); } finally { setOcupado(null); }
  }
  const texto = textoDoDoc(d);

  return (
    <Screen title={t('Documento')} onBack={sair}>
      {!aqui && d.paginas.length > 0 && (
        <View style={{ backgroundColor: alpha(cor, 0.12), borderRadius: 14, padding: 12, gap: 8 }}>
          <Text style={{ color: C.text }}>📱 {t('As fotos deste documento estão no dispositivo onde ele foi escaneado. Lá dá para editar, compartilhar e ler o texto.')}</Text>
          {!!d.salvo?.url && <Btn cor={cor} label={t('Abrir o PDF no {nome}', { nome: NOME[d.salvo.onde as 'google' | 'microsoft'] })} onPress={() => Linking.openURL(d.salvo!.url!)} />}
        </View>
      )}
      <TextInput value={nome} onChangeText={setNome} onBlur={salvarNome} onSubmitEditing={salvarNome} placeholder={t('Nome do documento')} placeholderTextColor={C.mute}
        style={{ color: C.text, fontSize: 20, fontWeight: '800', backgroundColor: C.card, borderRadius: 14, paddingHorizontal: 14, paddingVertical: 12 }} />
      <ScrollView horizontal showsHorizontalScrollIndicator={false} contentContainerStyle={{ gap: 8 }}>
        <Chip small cor={cor} on={!d.pasta} label={t('Sem pasta')} onPress={() => mudarDoc(d.id, { pasta: null })} />
        {pastas.map(p => <Chip key={p.id} small cor={cor} on={d.pasta === p.id} label={'📁 ' + p.nome} onPress={() => mudarDoc(d.id, { pasta: p.id })} />)}
      </ScrollView>

      <Section t={`${t('Páginas')} (${d.paginas.length})`} cor={cor} />
      {d.paginas.length > 1 && <Text style={{ color: C.mute, fontSize: 11 }}>{t('Toque numa página para recortar, girar ou mudar o filtro. Segure e arraste para mudar a ordem.')}</Text>}
      <Ordenavel itens={d.paginas} chave={p => p.id} grade gap={10} onOrdem={ps => ordenarPaginas(d.id, ps)}
        estiloItem={() => ({ width: largMini })}
        render={(p, i) => (
          <Pressable onPress={() => setPagina(i)} style={{ alignItems: 'center', gap: 4 }}>
            <Miniatura d={d} p={p} larg={largMini} cor={cor} />
            <Text style={{ color: C.sub, fontSize: 12 }}>{d.tipo === 'id' ? (i % 2 ? t('Verso') : t('Frente')) : i + 1}</Text>
          </Pressable>
        )} />
      {aqui && <Btn outline cor={cor} label={'＋ ' + t('Adicionar página')} onPress={adicionar} />}

      <Section t={t('Guardar e enviar')} cor={cor} />
      <Btn cor={cor} label={'💾 ' + t('Guardar o PDF {onde}', { onde: destino === 'aparelho' ? nomeDestino('aparelho') : t('no {nome}', { nome: NOME[destino] }) })}
        onPress={guardar} disabled={!d.paginas.length || !aqui} />
      {d.salvo && (
        <View style={{ flexDirection: 'row', alignItems: 'center', gap: 10 }}>
          <Text style={{ color: copiaEmDia(d) ? C.sub : C.warn, fontSize: 12, flex: 1 }}>
            {copiaEmDia(d) ? '✓ ' : '⚠️ '}{t('Guardado {onde} em {quando}', { onde: d.salvo.onde === 'aparelho' ? (d.salvo.pasta ? t('na pasta "{pasta}"', { pasta: d.salvo.pasta }) : nomeDestino('aparelho')) : t('no {nome}', { nome: NOME[d.salvo.onde] }),
              quando: `${brYmd(ymd(new Date(d.salvo.em)))} ${new Date(d.salvo.em).toTimeString().slice(0, 5)}` })}
            {copiaEmDia(d) ? '' : ' · ' + t('o documento mudou depois disso: guarde de novo')}
          </Text>
          {!!d.salvo.url && <Pressable onPress={() => Linking.openURL(d.salvo!.url!)}><Text style={{ color: cor, fontWeight: '700' }}>{t('Abrir')}</Text></Pressable>}
        </View>
      )}
      <Btn outline cor={cor} label={'📤 ' + t('Compartilhar PDF')} onPress={enviar} disabled={!d.paginas.length || !aqui} />
      <Text style={{ color: C.sub, fontSize: 12 }}>{t('Abre a janela do celular: WhatsApp, e-mail, Google Drive, OneDrive, salvar em Arquivos e outros.')}</Text>
      {d.tipo !== 'id' && (
        <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 8 }}>
          <Chip small cor={cor} on={(d.tamanho ?? 'a4') === 'a4'} label="A4" onPress={() => mudarDoc(d.id, { tamanho: 'a4' })} />
          <Chip small cor={cor} on={d.tamanho === 'carta'} label={t('Carta')} onPress={() => mudarDoc(d.id, { tamanho: 'carta' })} />
          <Chip small cor={cor} on={d.tamanho === 'foto'} label={t('Tamanho da foto')} onPress={() => mudarDoc(d.id, { tamanho: 'foto' })} />
        </View>
      )}
      <Chip small cor={cor} on={!!d.menor} label={(d.menor ? '✓ ' : '') + t('Arquivo menor (para WhatsApp e e-mail)')} onPress={() => mudarDoc(d.id, { menor: !d.menor })} />

      <Section t={t('Assinatura')} cor={cor} />
      {!d.assinatura && <Btn outline cor={cor} label={'✍️ ' + t('Assinar o documento')} onPress={() => setAssinar(true)} />}
      {d.assinatura && (
        <View style={{ backgroundColor: C.card, borderRadius: 14, padding: 12, gap: 10 }}>
          <View style={{ width: 180, alignSelf: 'center' }}>
            <DesenhoView tracos={d.assinatura.tracos} alt={d.assinatura.alt} larg={d.assinatura.larg} fundo="#fff" />
          </View>
          <Text style={{ color: C.sub, fontSize: 12 }}>{t('Em qual página e onde')}</Text>
          <ScrollView horizontal showsHorizontalScrollIndicator={false} contentContainerStyle={{ gap: 8 }}>
            {d.paginas.map((_, i) => (d.tipo === 'id' && i % 2 ? null : (
              <Chip key={i} small cor={cor} on={d.assinatura!.pagina === (d.tipo === 'id' ? i / 2 : i)} label={t('Pág. {n}', { n: (d.tipo === 'id' ? i / 2 : i) + 1 })}
                onPress={() => mudarDoc(d.id, { assinatura: { ...d.assinatura!, pagina: d.tipo === 'id' ? i / 2 : i } })} />
            )))}
          </ScrollView>
          <View style={{ flexDirection: 'row', gap: 8 }}>
            {(['esq', 'centro', 'dir'] as const).map(l => (
              <Chip key={l} small cor={cor} on={d.assinatura!.lugar === l} label={l === 'esq' ? t('Embaixo à esquerda') : l === 'centro' ? t('No centro') : t('À direita')}
                onPress={() => mudarDoc(d.id, { assinatura: { ...d.assinatura!, lugar: l } })} />
            ))}
          </View>
          <View style={{ flexDirection: 'row', gap: 16 }}>
            <Pressable onPress={() => setAssinar(true)}><Text style={{ color: cor, fontWeight: '700' }}>{t('Refazer')}</Text></Pressable>
            <Pressable onPress={() => mudarDoc(d.id, { assinatura: null })}><Text style={{ color: C.danger }}>{t('Tirar a assinatura')}</Text></Pressable>
          </View>
        </View>
      )}

      <Section t={t('Inteligência artificial')} cor={cor} />
      {!temIA && <Text style={{ color: C.sub, fontSize: 13 }}>{t('Ligue a sua IA em Ajustes › Inteligência artificial para ler o texto, resumir e perguntar sobre o documento.')}</Text>}
      <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 8 }}>
        <Btn outline cor={cor} label={'🔤 ' + t('Ler o texto')} onPress={() => (texto && d.paginas.every(p => p.texto != null) ? setVerTexto(v => !v) : ia('texto'))} style={{ flex: 1, minWidth: 140 }} />
        <Btn outline cor={cor} label={'✨ ' + t('Resumir')} onPress={() => ia('resumo')} style={{ flex: 1, minWidth: 140 }} />
      </View>
      {!!d.resumo && (
        <View style={{ backgroundColor: alpha(cor, 0.12), borderRadius: 14, padding: 12, gap: 6 }}>
          <Text style={{ color: C.text, fontWeight: '700' }}>✨ {t('Resumo')}</Text>
          <Text style={{ color: C.text }} selectable>{d.resumo}</Text>
        </View>
      )}
      {verTexto && !!texto && (
        <View style={{ backgroundColor: C.card, borderRadius: 14, padding: 12, gap: 8 }}>
          <View style={{ flexDirection: 'row', alignItems: 'center' }}>
            <Text style={{ color: C.text, fontWeight: '700', flex: 1 }}>🔤 {t('Texto do documento')}</Text>
            <Pressable onPress={() => Share.share({ message: texto }).catch(() => {})}><Text style={{ color: cor, fontWeight: '700' }}>{t('Compartilhar texto')}</Text></Pressable>
          </View>
          <Text style={{ color: C.text, fontSize: 13 }} selectable>{texto}</Text>
          <Text style={{ color: C.mute, fontSize: 11 }}>{t('O texto também vai escondido dentro do PDF: dá para buscar e copiar no leitor de PDF.')}</Text>
        </View>
      )}
      <View style={{ flexDirection: 'row', gap: 8, alignItems: 'center' }}>
        <View style={{ flex: 1 }}><Field value={pergunta} onChangeText={setPergunta} placeholder={t('Pergunte sobre o documento (ex.: qual o prazo?)')} onSubmitEditing={() => ia('pergunta')} /></View>
        <Pressable onPress={() => ia('pergunta')} style={{ backgroundColor: cor, borderRadius: 12, paddingHorizontal: 14, paddingVertical: 12 }}><Text style={{ color: '#fff', fontWeight: '800' }}>💬</Text></Pressable>
      </View>
      {!!resposta && <Text style={{ color: C.text, backgroundColor: C.card, borderRadius: 14, padding: 12 }} selectable>{resposta}</Text>}
      <Text style={{ color: C.mute, fontSize: 11 }}>{t('As imagens vão só para a IA que você escolheu, com a sua chave.')}</Text>

      <Pressable onPress={() => Alert.alert(t('Excluir {nome}?', { nome: d.nome }), t('O PDF e as imagens saem deste dispositivo e da nuvem.') + '\n\n' + t('Fica 30 dias na lixeira.'), [
        { text: t('Cancelar'), style: 'cancel' }, { text: t('Excluir'), style: 'destructive', onPress: () => {
          onBack(); paraLixeira('scanner', 'lista', d.nome, d, { doc: 'scanner', campo: 'docs' }); apagarDoc(d.id); } }])}
        style={{ alignItems: 'center', padding: 14, marginTop: 10 }}>
        <Text style={{ color: C.danger }}>🗑 {t('Excluir o documento')}</Text>
      </Pressable>

      <PaginaTela d={d} indice={pagina} cor={cor} onIndice={setPagina} setOcupado={setOcupado} />
      <DesenhoSheet visible={assinar} cor={cor} alt={400} assinatura onClose={() => setAssinar(false)}
        onSalvar={(tracos, alt, larg) => {
          setAssinar(false);
          if (!tracos.length) return;
          mudarDoc(d.id, { assinatura: { tracos, alt, larg, pagina: d.assinatura?.pagina ?? Math.max(0, (d.tipo === 'id' ? Math.ceil(d.paginas.length / 2) : d.paginas.length) - 1), lugar: d.assinatura?.lugar ?? 'dir' } });
        }} />
    </Screen>
  );
}

// ─── uma página em tela cheia: recortar, girar, filtro e excluir ───
function PaginaTela({ d, indice, cor, onIndice, setOcupado }: { d: ScanDoc; indice: number | null; cor: string; onIndice: (i: number | null) => void; setOcupado: (s: string | null) => void }) {
  const ins = useSafeAreaInsets();
  const p = indice != null ? d.paginas[indice] : undefined;
  const [ajustar, setAjustar] = useState<{ uri: string; inicial: Cantos | null } | null>(null);
  const pode = temTratamento();
  const fechar = () => onIndice(null);
  useEffect(() => { if (indice != null && !p) onIndice(d.paginas.length ? d.paginas.length - 1 : null); }, [indice, p, d.paginas.length]); // eslint-disable-line react-hooks/exhaustive-deps

  const mudar = useCallback(async (m: Parameters<typeof retratar>[2]) => {
    if (!p) return;
    if (!pode) { Alert.alert(t('Disponível no app novo'), t('Recortar, girar e os filtros chegam com o app novo (versão 0.49).')); return; }
    setOcupado(t('Melhorando a imagem...'));
    try { await retratar(d.id, p.id, m); } catch (e) { falha(e); } finally { setOcupado(null); }
  }, [p, d.id, pode, setOcupado]);

  return (
    <Modal visible={!!p} animationType="fade" onRequestClose={fechar}>
      <View style={{ flex: 1, backgroundColor: '#0B0B0F', paddingTop: ins.top + 6, paddingBottom: ins.bottom + 10 }}>
        <View style={{ flexDirection: 'row', alignItems: 'center', paddingHorizontal: 16, paddingVertical: 10 }}>
          <Pressable onPress={fechar} hitSlop={12}><Text style={{ color: '#bbb', fontSize: 15 }}>{t('Fechar')}</Text></Pressable>
          <Text style={{ flex: 1, textAlign: 'center', color: '#fff', fontWeight: '700', fontSize: 16 }}>
            {indice != null ? t('Página {n} de {total}', { n: indice + 1, total: d.paginas.length }) : ''}
          </Text>
          <Pressable hitSlop={12} onPress={() => p && Alert.alert(t('Excluir esta página?'), '', [{ text: t('Cancelar'), style: 'cancel' },
            { text: t('Excluir'), style: 'destructive', onPress: () => { apagarPagina(d.id, p.id); if (d.paginas.length <= 1) fechar(); } }])}>
            <Text style={{ color: '#ff8a8a', fontSize: 15 }}>🗑</Text>
          </Pressable>
        </View>
        <View style={{ flex: 1, flexDirection: 'row', alignItems: 'center' }}>
          <Pressable disabled={!indice} onPress={() => onIndice((indice ?? 1) - 1)} style={{ padding: 10, opacity: indice ? 1 : 0.2 }}><Text style={{ color: '#fff', fontSize: 30 }}>‹</Text></Pressable>
          <View style={{ flex: 1, height: '100%' }}>{p && <Imagem d={d} p={p} cor={cor} />}</View>
          <Pressable disabled={indice == null || indice >= d.paginas.length - 1} onPress={() => onIndice((indice ?? 0) + 1)}
            style={{ padding: 10, opacity: indice != null && indice < d.paginas.length - 1 ? 1 : 0.2 }}><Text style={{ color: '#fff', fontSize: 30 }}>›</Text></Pressable>
        </View>
        {p && (
          <View style={{ gap: 10, paddingHorizontal: 12, paddingTop: 10 }}>
            <ScrollView horizontal showsHorizontalScrollIndicator={false} contentContainerStyle={{ gap: 8, paddingHorizontal: 4 }}>
              {(['documento', 'original', 'cinza', 'pb'] as FiltroScan[]).map(f => (
                <Pressable key={f} onPress={() => (p.filtro ?? 'original') !== f && mudar({ filtro: f })}
                  style={{ paddingHorizontal: 14, paddingVertical: 8, borderRadius: 20, backgroundColor: (p.filtro ?? 'original') === f ? cor : 'rgba(255,255,255,0.12)' }}>
                  <Text style={{ color: '#fff', fontWeight: '600' }}>{t(NOMES_FILTRO[f])}</Text>
                </Pressable>
              ))}
            </ScrollView>
            <View style={{ flexDirection: 'row', justifyContent: 'space-around' }}>
              <Ferramenta icone="✂️" rotulo={t('Recortar')} onPress={async () => {
                if (!pode) return mudar({});
                const u = await fonteBruta(d.id, p);
                if (!u) return falha(new Error(t('A imagem original não está neste dispositivo.')));
                setAjustar({ uri: u, inicial: (p.cantos as Cantos | null | undefined) ?? (await cantosAutomaticos(u)) });
              }} />
              <Ferramenta icone="↺" rotulo={t('Girar')} onPress={() => mudar({ giro: ((p.giro ?? 0) + 270) % 360 })} />
              <Ferramenta icone="↻" rotulo={t('Girar')} onPress={() => mudar({ giro: ((p.giro ?? 0) + 90) % 360 })} />
            </View>
            {!!p.texto && <Text style={{ color: '#ccc', fontSize: 12 }} numberOfLines={3}>🔤 {p.texto}</Text>}
          </View>
        )}
      </View>
      <AjustarCantos uri={ajustar?.uri ?? null} inicial={ajustar?.inicial} cor={cor} onCancelar={() => setAjustar(null)}
        onAplicar={c => { setAjustar(null); mudar({ cantos: c }); }} />
    </Modal>
  );
}

function Ferramenta({ icone, rotulo, onPress }: { icone: string; rotulo: string; onPress: () => void }) {
  return (
    <Pressable onPress={onPress} style={{ alignItems: 'center', gap: 2, paddingHorizontal: 14, paddingVertical: 6 }}>
      <Text style={{ color: '#fff', fontSize: 22 }}>{icone}</Text>
      <Text style={{ color: '#ddd', fontSize: 12 }}>{rotulo}</Text>
    </Pressable>
  );
}

/** Endereço da imagem da página (deste aparelho ou da nuvem), atualizado quando a página muda. */
function useFonte(d: ScanDoc, p?: ScanPagina) {
  const [u, setU] = useState<string | null>(null);
  useEffect(() => {
    let vivo = true;
    if (p) fontePagina(d.id, p).then(x => { if (vivo) setU(x); }).catch(() => {});
    return () => { vivo = false; };
  }, [d.id, p?.id, p?.v, p?.local?.final]); // eslint-disable-line react-hooks/exhaustive-deps
  return u;
}

function Miniatura({ d, p, larg, cor }: { d: ScanDoc; p?: ScanPagina; larg: number; cor: string }) {
  const u = useFonte(d, p);
  const alt = Math.round(larg * 1.3);
  return (
    <View style={{ width: larg, height: alt, borderRadius: 8, overflow: 'hidden', backgroundColor: C.card2, alignItems: 'center', justifyContent: 'center', borderWidth: 1, borderColor: C.line }}>
      {u ? <Image source={{ uri: u }} style={{ width: larg, height: alt }} resizeMode="cover" /> : p ? <ActivityIndicator color={cor} /> : <Text>📄</Text>}
    </View>
  );
}

function Imagem({ d, p, cor }: { d: ScanDoc; p: ScanPagina; cor: string }) {
  const u = useFonte(d, p);
  if (!u) return <ActivityIndicator color={cor} style={{ marginTop: 80 }} />;
  return <Image source={{ uri: u }} style={{ width: '100%', height: '100%' }} resizeMode="contain" />;
}

function Ocupado({ msg, cor }: { msg: string | null; cor: string }) {
  return (
    <Modal visible={!!msg} transparent animationType="fade" onRequestClose={() => {}}>
      <View style={{ flex: 1, backgroundColor: 'rgba(0,0,0,0.55)', alignItems: 'center', justifyContent: 'center' }}>
        <View style={{ backgroundColor: C.card, borderRadius: 18, padding: 22, gap: 12, alignItems: 'center', minWidth: 220, maxWidth: 320 }}>
          <ActivityIndicator color={cor} size="large" />
          <Text style={{ color: C.text, textAlign: 'center' }}>{msg}</Text>
        </View>
      </View>
    </Modal>
  );
}

function NomeModal({ dados, cor, onFechar }: { dados: { titulo: string; valor: string; ok: (s: string) => void } | null; cor: string; onFechar: () => void }) {
  const [v, setV] = useState('');
  useEffect(() => { if (dados) setV(dados.valor); }, [dados]);
  const ok = () => { dados?.ok(v); onFechar(); };
  return (
    <Modal visible={!!dados} transparent animationType="fade" onRequestClose={onFechar}>
      <Pressable onPress={onFechar} style={{ flex: 1, backgroundColor: C.overlay, justifyContent: 'center', padding: 24 }}>
        <Pressable style={{ backgroundColor: C.card, borderRadius: 18, padding: 18, gap: 12 }}>
          <Text style={{ color: C.text, fontWeight: '700', fontSize: 16 }}>{dados?.titulo}</Text>
          <Field value={v} onChangeText={setV} autoFocus placeholder={t('Nome da pasta (ex.: Recibos, Contratos)')} onSubmitEditing={ok} />
          <View style={{ flexDirection: 'row', justifyContent: 'flex-end', gap: 20 }}>
            <Pressable onPress={onFechar}><Text style={{ color: C.sub }}>{t('Cancelar')}</Text></Pressable>
            <Pressable onPress={ok}><Text style={{ color: cor, fontWeight: '800' }}>{t('Salvar')}</Text></Pressable>
          </View>
        </Pressable>
      </Pressable>
    </Modal>
  );
}
